import { useIntl } from 'react-intl';

import { Alert, Box, IconButton, paletteColors, Slider, Typography } from '@evaneos/b2b-ui';
import { alpha } from '@evaneos/b2b-ui';
import Pause from '@evaneos/b2b-ui/icons/Pause';
import PlayArrow from '@evaneos/b2b-ui/icons/PlayArrow';
import VolumeOff from '@evaneos/b2b-ui/icons/VolumeOff';
import VolumeUp from '@evaneos/b2b-ui/icons/VolumeUp';

import { useAudioPlayer } from './useAudioPlayer';

type AudioPlayerProps = {
    audioUrl: string;
    isDeleted: boolean;
    phoneCallId: number;
};

export const AudioPlayer = ({ audioUrl, isDeleted, phoneCallId }: AudioPlayerProps) => {
    const { formatMessage } = useIntl();
    const {
        audioRef,
        isPlaying,
        currentTime,
        duration,
        volume,
        isReady,
        handlePlayPause,
        handleSeek,
        handleSeekCommitted,
        handleKeyDown,
        handleVolumeChange,
        toggleMute,
        formatTime,
    } = useAudioPlayer({ audioUrl, isDeleted, phoneCallId });

    return (
        <Box>
            <Box component="audio" ref={audioRef} preload="metadata" sx={{ display: 'none' }} />

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    paddingX: 2,
                    paddingY: 0.25,
                    opacity: isDeleted ? 0.5 : 1,
                    '&:focus-visible': { bgcolor: paletteColors.neutral[100] },
                }}
                role="region"
                aria-label={formatMessage({
                    id: 'dossier.discussion.phone_call.audio_player_region_label',
                })}
                onKeyDown={handleKeyDown}
                tabIndex={0}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        onClick={() => void handlePlayPause()}
                        disabled={isDeleted}
                        aria-label={formatMessage({
                            id: isPlaying
                                ? 'dossier.discussion.phone_call.audio_player.pause_button_label'
                                : 'dossier.discussion.phone_call.audio_player.play_button_label',
                        })}
                        size="small"
                        sx={{ borderRadius: 1, color: 'icon.selected' }}
                    >
                        {isPlaying ? <Pause /> : <PlayArrow />}
                    </IconButton>

                    <Typography
                        variant="body2"
                        sx={{
                            minWidth: 56,
                            fontWeight: 700,
                            fontSize: '1.2rem',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>

                    <Box
                        sx={{
                            flex: 1,
                            marginLeft: 'auto',
                            paddingLeft: 1,
                            maxWidth: '200px',
                            minHeight: 34,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Slider
                            value={currentTime}
                            min={0}
                            max={duration || 0}
                            onChange={handleSeek}
                            onChangeCommitted={handleSeekCommitted}
                            disabled={isDeleted || !isReady}
                            aria-label={formatMessage({
                                id: 'dossier.discussion.phone_call.audio_player.progress_slider_label',
                            })}
                            aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
                            size="small"
                            sx={{
                                '& .MuiSlider-thumb': {
                                    width: 8,
                                    height: 8,
                                    '&:hover, &.Mui-active, &.Mui-focusVisible': {
                                        boxShadow: `0px 0px 0px 8px ${alpha(paletteColors.green[700]!, 0.16)}`,
                                    },
                                },
                                '& .MuiSlider-rail': {
                                    backgroundColor: paletteColors.neutral[300],
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            '& > [data-volume-slider]': {
                                width: 0,
                                opacity: 0,
                                transition: 'width 0.3s ease-out, opacity 0.3s ease-out',
                            },
                            '&:hover > [data-volume-slider], &:focus-within > [data-volume-slider]':
                                {
                                    width: 80,
                                    opacity: 1,
                                },
                        }}
                    >
                        <IconButton
                            onClick={toggleMute}
                            disabled={isDeleted}
                            aria-label={formatMessage({
                                id:
                                    volume === 0
                                        ? 'dossier.discussion.phone_call.audio_player.unmute_button_label'
                                        : 'dossier.discussion.phone_call.audio_player.mute_button_label',
                            })}
                            size="small"
                            sx={{ borderRadius: 1, color: 'icon.selected' }}
                        >
                            {volume === 0 ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                        <Box
                            data-volume-slider
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                                px: 2,
                                mx: -2,
                            }}
                        >
                            <Slider
                                value={volume}
                                min={0}
                                max={1}
                                step={0.1}
                                onChange={handleVolumeChange}
                                disabled={isDeleted}
                                aria-label={formatMessage({
                                    id: 'dossier.discussion.phone_call.audio_player.volume_slider_label',
                                })}
                                size="small"
                                sx={{
                                    width: 70,
                                    '& .MuiSlider-thumb': {
                                        width: 8,
                                        height: 8,
                                        '&:hover, &.Mui-active, &.Mui-focusVisible': {
                                            boxShadow: `0px 0px 0px 8px ${alpha(paletteColors.green[700]!, 0.16)}`,
                                        },
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: paletteColors.neutral[300],
                                        opacity: 1,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {isDeleted && (
                <Alert severity="info" sx={{ mt: 2 }} data-testid="audio-deleted-alert">
                    {formatMessage({
                        id: 'dossier.discussion.phone_call.audio_file_not_available',
                    })}
                </Alert>
            )}
        </Box>
    );
};
